import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { 
  Ticket, Clock, CheckCircle2, AlertCircle, Users, Video, Monitor, 
  ExternalLink, UserCheck, Filter, Package, Mail, Trash2
} from "lucide-react";

const priorityColors = {
  low: "bg-slate-100 text-slate-700",
  medium: "bg-blue-100 text-blue-700",
  high: "bg-amber-100 text-amber-700",
  urgent: "bg-red-100 text-red-700",
};

const statusColors = {
  open: "bg-blue-100 text-blue-700",
  in_progress: "bg-amber-100 text-amber-700",
  pending_client: "bg-purple-100 text-purple-700",
  resolved: "bg-emerald-100 text-emerald-700",
  closed: "bg-slate-100 text-slate-700",
};

const statusLabels = {
  open: "Open",
  in_progress: "In Progress",
  pending_client: "Awaiting Client",
  resolved: "Resolved",
  closed: "Closed",
};

export default function ConsultantDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({});
  const [tickets, setTickets] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [productFilter, setProductFilter] = useState("all");
  const [selectedTickets, setSelectedTickets] = useState(new Set());

  const toggleSelectTicket = (id) => {
    setSelectedTickets(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleBulkDelete = async () => {
    if (selectedTickets.size === 0) return;
    if (!window.confirm(`Delete ${selectedTickets.size} ticket(s)? This cannot be undone.`)) return;
    try {
      await apiClient.post("/tickets/bulk-delete", { ticket_ids: Array.from(selectedTickets) });
      toast.success(`${selectedTickets.size} ticket(s) deleted`);
      setSelectedTickets(new Set());
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete tickets");
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [statsRes, ticketsRes, productsRes] = await Promise.all([
        apiClient.get("/stats/dashboard"),
        apiClient.get("/tickets"),
        apiClient.get("/products?active_only=true"),
      ]);
      setStats(statsRes.data);
      setTickets(ticketsRes.data);
      setProducts(productsRes.data);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAssignToMe = async (ticketId, e) => {
    e.stopPropagation();
    try {
      await apiClient.patch(`/tickets/${ticketId}`, {
        assigned_to: user.id,
        status: "in_progress",
      });
      toast.success("Ticket assigned to you");
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail || "Failed to assign ticket";
      if (detail.includes("Payment") || detail.includes("payment")) {
        toast.error("Cannot assign — payment has not been confirmed for this ticket");
      } else {
        toast.error(detail);
      }
    }
  };

  const handleSendPaymentLink = async (ticketId, e) => {
    e.stopPropagation();
    try {
      await apiClient.post(`/tickets/${ticketId}/send-payment-request`, { message: "" });
      toast.success("Payment link sent to client");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send payment link");
    }
  };

  const filteredTickets = tickets.filter((ticket) => {
    let matches = true;
    
    if (filter === "assigned") matches = matches && ticket.assigned_to === user?.id;
    if (filter === "unassigned") matches = matches && !ticket.assigned_to;
    if (filter === "open") matches = matches && ticket.status === "open";
    if (filter === "in_progress") matches = matches && ticket.status === "in_progress";
    if (filter === "pending_payment") matches = matches && ticket.payment_status === "pending_payment";
    
    if (productFilter !== "all") {
      matches = matches && ticket.product_id === productFilter;
    }
    
    return matches;
  });

  const sourceIcons = {
    portal: Ticket,
    email: Mail,
  };

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <Card className="sag-card">
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl ${color}`}>
            <Icon className="h-6 w-6" />
          </div>
          <div>
            <p className="text-sm text-slate-500 font-medium">{label}</p>
            <p className="text-2xl font-bold font-heading text-slate-900">{value || 0}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <DashboardLayout>
      <div className="space-y-8" data-testid="consultant-dashboard">
        {/* Header */}
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">Consultant Dashboard</h1>
          <p className="text-slate-500 mt-1">Manage tickets and provide support to clients</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard icon={UserCheck} label="My Tickets" value={stats.assigned_tickets} color="bg-sag-navy/10 text-sag-navy" />
          <StatCard icon={AlertCircle} label="Unassigned" value={stats.unassigned_tickets} color="bg-amber-100 text-amber-600" />
          <StatCard icon={Clock} label="In Progress" value={stats.in_progress} color="bg-blue-100 text-blue-600" />
          <StatCard icon={CheckCircle2} label="Resolved Today" value={stats.resolved_today} color="bg-emerald-100 text-emerald-600" />
        </div>

        {/* Tickets Section */}
        <Card className="sag-card">
          <CardHeader className="border-b border-slate-100">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <CardTitle className="font-heading text-xl">Support Queue</CardTitle>
                <CardDescription>Manage and respond to client tickets</CardDescription>
              </div>
              <div className="flex items-center gap-2">
                {user?.role === "admin" && selectedTickets.size > 0 && (
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={handleBulkDelete}
                    data-testid="bulk-delete-tickets"
                  >
                    <Trash2 className="h-4 w-4 mr-1" /> Delete ({selectedTickets.size})
                  </Button>
                )}
                <Filter className="h-4 w-4 text-slate-400" />
                <Select value={filter} onValueChange={setFilter}>
                  <SelectTrigger className="w-[160px]" data-testid="ticket-filter-select">
                    <SelectValue placeholder="Filter tickets" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Tickets</SelectItem>
                    <SelectItem value="assigned">Assigned to Me</SelectItem>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="pending_payment">Pending Payment</SelectItem>
                  </SelectContent>
                </Select>
                {products.length > 0 && (
                  <Select value={productFilter} onValueChange={setProductFilter}>
                    <SelectTrigger className="w-[180px]" data-testid="product-filter-select">
                      <SelectValue placeholder="All Products" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Products</SelectItem>
                      {products.map((product) => (
                        <SelectItem key={product.id} value={product.id}>
                          {product.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-slate-500">Loading tickets...</div>
            ) : filteredTickets.length === 0 ? (
              <div className="p-8 text-center">
                <Ticket className="h-12 w-12 mx-auto text-slate-300 mb-4" />
                <p className="text-slate-500">No tickets match your filter</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {filteredTickets.map((ticket) => {
                  const SourceIcon = sourceIcons[ticket.source] || Ticket;
                  return (
                  <div
                    key={ticket.id}
                    className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                    data-testid={`consultant-ticket-${ticket.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      {user?.role === "admin" && (
                        <input
                          type="checkbox"
                          checked={selectedTickets.has(ticket.id)}
                          onChange={(e) => { e.stopPropagation(); toggleSelectTicket(ticket.id); }}
                          onClick={(e) => e.stopPropagation()}
                          className="mt-1.5 h-4 w-4 rounded border-slate-300 text-sag-navy focus:ring-sag-navy cursor-pointer"
                          data-testid={`select-ticket-${ticket.id}`}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <SourceIcon className="h-4 w-4 text-slate-400" />
                          <h3 className="font-semibold text-slate-900 truncate">{ticket.title}</h3>
                          {ticket.priority === "urgent" && (
                            <span className="animate-pulse">
                              <AlertCircle className="h-4 w-4 text-red-500" />
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-500 line-clamp-1">{ticket.description}</p>
                        <div className="flex items-center gap-3 mt-3 flex-wrap">
                          <Badge className={statusColors[ticket.status]} variant="secondary">
                            {statusLabels[ticket.status]}
                          </Badge>
                          <Badge className={priorityColors[ticket.priority]} variant="secondary">
                            {ticket.priority}
                          </Badge>
                          {ticket.payment_status === "pending_payment" && (
                            <Badge className="bg-red-100 text-red-700 border border-red-200" variant="secondary" data-testid={`payment-badge-${ticket.id}`}>
                              $ Unpaid
                            </Badge>
                          )}
                          {ticket.payment_status === "covered_by_sla" && (
                            <Badge className="bg-blue-100 text-blue-700 border border-blue-200" variant="secondary">
                              SLA
                            </Badge>
                          )}
                          {ticket.payment_status === "covered_by_product" && (
                            <Badge className="bg-teal-100 text-teal-700 border border-teal-200" variant="secondary">
                              Product
                            </Badge>
                          )}
                          {ticket.payment_status === "paid" && (
                            <Badge className="bg-emerald-100 text-emerald-700 border border-emerald-200" variant="secondary">
                              Paid
                            </Badge>
                          )}
                          {ticket.product_name && (
                            <Badge variant="outline" className="border-sag-blue text-sag-blue">
                              <Package className="h-3 w-3 mr-1" />
                              {ticket.product_name}
                            </Badge>
                          )}
                          <span className="text-xs text-slate-500">
                            Client: {ticket.client_name}
                          </span>
                          {ticket.assigned_name && (
                            <span className="text-xs text-slate-500">
                              <UserCheck className="h-3 w-3 inline mr-1" />
                              {ticket.assigned_name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {ticket.payment_status === "pending_payment" && (
                          <Button
                            size="sm"
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            onClick={(e) => handleSendPaymentLink(ticket.id, e)}
                            data-testid={`send-payment-${ticket.id}`}
                          >
                            <Mail className="h-3.5 w-3.5 mr-1" /> Send Payment Link
                          </Button>
                        )}
                        {!ticket.assigned_to && ticket.payment_status !== "pending_payment" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => handleAssignToMe(ticket.id, e)}
                            data-testid={`assign-ticket-${ticket.id}`}
                          >
                            Assign to Me
                          </Button>
                        )}
                        {!ticket.assigned_to && ticket.payment_status === "pending_payment" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="opacity-50 cursor-not-allowed"
                            disabled
                            data-testid={`assign-ticket-${ticket.id}`}
                          >
                            Awaiting Payment
                          </Button>
                        )}
                        <ExternalLink className="h-5 w-5 text-slate-400" />
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="sag-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Video className="h-5 w-5 text-sag-blue" />
                Video Consultations
              </CardTitle>
              <CardDescription>Start or join video calls with clients</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 mb-4">
                Create a video room from a ticket detail page to start a consultation.
              </p>
              <Button variant="outline" disabled className="w-full">
                Select a ticket to start
              </Button>
            </CardContent>
          </Card>

          <Card className="sag-card">
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Monitor className="h-5 w-5 text-sag-accent" />
                Remote Assistance
              </CardTitle>
              <CardDescription>Access client screens for support</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-slate-500 mb-4">
                Start a remote session from a ticket to view and control client screens.
              </p>
              <Button variant="outline" disabled className="w-full">
                Select a ticket to start
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
